import axios from 'axios';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
// HA-140: Telehealth provider types
export type TelehealthProvider = 'Zoom' | 'Chime';

// API service for practice profile and onboarding
export interface PracticeProfile {
  practiceId?: string; // The practice identifier from JWT (custom:practice_name)
  practiceName?: string;
  clientNumbers?: number;
  teamSize?: number;
  practiceWebsite?: string;
  isFirstPractice: boolean;
  hasCompletedOnboarding?: boolean;
  onboardingCompletedDate?: Date;
  trialStartDate: Date;
  trialEndDate: Date;
  hasPaidPlan: boolean;
  // HA-140: Default telehealth provider
  defaultTelehealthProvider?: TelehealthProvider;
}

const practiceProfileApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/UserPracticeProfile`,
  withCredentials: true,
});

// Add authorization header to all requests
installSessionInterceptors(practiceProfileApi);

// Get the practice profile for the current user
export const getPracticeProfile = async (): Promise<PracticeProfile | null> => {
  try {
    const response = await practiceProfileApi.get('');

    return response.data;
  } catch (error) {
    console.error('Error fetching practice profile:', error);

    return null;
  }
};

// Save the practice profile
export const savePracticeProfile = async (
  profile: PracticeProfile
): Promise<PracticeProfile | null> => {
  try {
    const response = await practiceProfileApi.post('', profile);

    if (response.status === 204 || response.data === undefined || response.data === '') {
      // Backend can omit a body on success; fall back to the submitted profile
      return profile;
    }

    return response.data;
  } catch (error) {
    console.error('Error saving practice profile:', error);

    return null;
  }
};

// Check if the user has completed onboarding
export const hasCompletedOnboarding = async (): Promise<boolean> => {
  try {
    const response = await practiceProfileApi.get('/OnboardingStatus');

    return response.data;
  } catch (error) {
    console.error('Error checking onboarding status:', error);

    return false;
  }
};

// Mark onboarding as complete
export const completeOnboarding = async (): Promise<PracticeProfile | null> => {
  try {
    const response = await practiceProfileApi.post('/CompleteOnboarding');

    return response.data;
  } catch (error) {
    console.error('Error completing onboarding:', error);

    return null;
  }
};
